/**
 * INPUT
 *
 * Turns keyboard, mouse-mode, touch and gamepad input into the six network keys
 * the server understands, plus the client-side control keys that UI.ts acts on.
 * Every path that presses or releases a key goes through setBindHeld, which
 * keeps the one record of what is held and sends only the changes.
 *
 * Keybinds are stored by key NAME (the labels in keyCodeByName), matched by
 * KeyboardEvent.code, and saved as the entries that differ from the defaults.
 */
import { GAME_TYPES } from '@airmash/protocol';
import { uiState, type KeybindItem } from '../ui/UIState';
import { cancelAutoPilotOnUserInput, isAutoPilotHolding } from './AutoPilot';
import { clock } from './Clock';
import { GAME_DEFAULTS } from './Config';
import { HALF_PI, PlaneType, TWO_PI } from './Constants';
import { device } from './Device';
import { SHIPS } from './GameData';
import { sendKey as networkSendKey, timing as networkTiming } from './Network';
import { getMe as playersGetMe } from './Players';
import { GAME_STATE_CODES, session } from './Session';
import { removeSetting, setSettings, settings } from './Settings';
import { spectatorOnStartHandleMovementIntent } from './SpectatorOnStart';
import type { BindName, BindSlot, KeyBinds, KeyBindsConfig, KeyBindTuple, KeyName, SOCDMode } from './types/Input';
import {
  chatBoxOpen as uiChatBoxOpen,
  closeAllPanels as uiCloseAllPanels,
  controlKey as uiControlKey,
  hideHelp as uiHideHelp,
  setActivePanel as uiSetActivePanel,
  setGameNotification as uiSetGameNotification,
} from './UI';

/** What the gamepad reported last frame, so only changes are sent. */
interface GamepadState {
  /** The stick is pushed past the deadzone, which holds UP. */
  forward: boolean;
  left: boolean;
  right: boolean;
  up: boolean;
  down: boolean;
  fire: boolean;
  special: boolean;
}

function freshGamepadState(): GamepadState {
  return { forward: false, left: false, right: false, up: false, down: false, fire: false, special: false };
}

/**
 * Binds currently reported as held. For the six network keys that is what the
 * server has been told. A strafe bind is remembered here while its SPECIAL and
 * turn keys are what the server sees, and a non-Mohawk's strafe sends nothing.
 */
const heldBinds = new Set<BindName>();
/** Physical keys down that resolve to a movement bind: the SOCD hold check and clearKeys' arrow rule read it. */
const pressedCodes = new Set<string>();
/** Control keys down since their press fired, so auto-repeat does not fire them again. */
const latchedControlCodes = new Set<string>();
let maybeRotationTimerId: number | null = null;
let lastRotationTime = 0;
/** Latched turn direction, used to stop LEFT/RIGHT flip-flop when the target is
 *  near ±180° (where angleDiff's sign is ambiguous). null = unlatched. */
let activeTurnDirection: 'LEFT' | 'RIGHT' | null = null;
let isGamepadConnected = false;
let gamepadIndex = 0;
let gamepadState = freshGamepadState();
let editingBindName: BindName | null = null;
let editingBindIndex: BindSlot = 0;
let cruiseMode = false;

/** The shipped keybinds. The live table starts as a copy, and resetBinds returns it to one. */
const DEFAULT_BINDS: Readonly<KeyBinds> = {
  LEFT: ['LEFT', 'A'],
  RIGHT: ['RIGHT', 'D'],
  UP: ['UP', 'W'],
  DOWN: ['DOWN', 'S'],
  STRAFELEFT: ['Q'],
  STRAFERIGHT: ['E'],
  FIRE: ['SPACE'],
  SPECIAL: ['CTRL', 'SHIFT'],
  SHOWSCORE: ['TAB'],
  DROP: ['Y'],
  MAINMENU: ['F1'],
  SHOWGAMES: ['F2'],
  EMOTES: ['F5'],
  LASER: ['P'],
  FULLSCREEN: ['F'],
  MINIMIZECHAT: ['PGDOWN'],
  MAXIMIZECHAT: ['PGUP'],
  SAY: ['GRAVE'],
  TEAM: ['T'],
  REPLY: ['R'],
  SPECTATE: ['V'],
  UPGRADE1: ['1'],
  UPGRADE2: ['2'],
  UPGRADE3: ['3'],
  UPGRADE4: ['4'],
  SOUND: ['G'],
  HELP: ['H'],
  KEYBINDS: ['K'],
  INVITE: ['I'],
  MOUSEMODE: ['M'],
  ALTZOOM: ['BACKSPACE'],
  ZOOMOUT: ['-'],
  ZOOMIN: ['='],
  DEFAULTZOOM: ['0'],
  CRUISE: ['5'],
  HIDEUI: ['F4'],
  CLEARCHAT: ['DELETE'],
  LEADERBOARD: ['F3'],
};

let keyBindDescription: KeyBinds = structuredClone(DEFAULT_BINDS);

/** Binds handled as held keys (deduped and sent while down) rather than as one-shot control keys. */
const MOVEMENT_BINDS: ReadonlySet<BindName> = new Set<BindName>([
  'LEFT',
  'RIGHT',
  'UP',
  'DOWN',
  'FIRE',
  'SPECIAL',
  'STRAFELEFT',
  'STRAFERIGHT',
]);

/** Opposing directional binds for SOCD resolution. */
const OPPOSING_BIND: Readonly<Partial<Record<BindName, BindName>>> = {
  UP: 'DOWN',
  DOWN: 'UP',
  LEFT: 'RIGHT',
  RIGHT: 'LEFT',
  STRAFELEFT: 'STRAFERIGHT',
  STRAFERIGHT: 'STRAFELEFT',
};

/** The six keys the server knows, in the order clearKeys releases them. */
const NETWORK_KEYS: readonly KeyName[] = ['UP', 'DOWN', 'LEFT', 'RIGHT', 'FIRE', 'SPECIAL'];
const NETWORK_KEY_SET: ReadonlySet<BindName> = new Set<BindName>(NETWORK_KEYS);

function isKeyName(bind: BindName): bind is KeyName {
  return NETWORK_KEY_SET.has(bind);
}

const BIND_NAME_SET: ReadonlySet<string> = new Set(Object.keys(DEFAULT_BINDS));

/** Whether a saved keybind entry names an action that still exists. */
function isBindName(name: string): name is BindName {
  return BIND_NAME_SET.has(name);
}

/** Every action, in the order of the defaults table. */
const BIND_NAMES: readonly BindName[] = Object.keys(DEFAULT_BINDS).filter(isBindName);

/** Whether a saved keybind entry has the tuple shape: one or two key names. */
function isKeyBindTuple(value: unknown): value is KeyBindTuple {
  return (
    Array.isArray(value) &&
    (value.length === 1 || value.length === 2) &&
    value.every((k: unknown) => typeof k === 'string')
  );
}

/** Physical key code → bind, rebuilt from the live table by updateKeybindsList. */
const bindByKeyCode = new Map<string, BindName>();

function isBindPhysicallyHeld(bind: BindName): boolean {
  for (const code of pressedCodes) {
    if (bindByKeyCode.get(code) === bind) {
      return true;
    }
  }
  return false;
}

function getSOCDMode(): SOCDMode {
  // Strict comparison doubles as a legacy guard: settings persisted before
  // the binary type (e.g. the removed 'neutral' mode) read as 'off'.
  return settings.game?.socdMode === 'lastInput' ? 'lastInput' : 'off';
}

/** [display name, bind] rows of the keybinds panel, in display order. */
const keybindPanelRows: ReadonlyArray<readonly [string, BindName]> = [
  ['Forward', 'UP'],
  ['Backward', 'DOWN'],
  ['Turn Left', 'LEFT'],
  ['Turn Right', 'RIGHT'],
  ['Fire', 'FIRE'],
  ['Special', 'SPECIAL'],
  ['Strafe Left', 'STRAFELEFT'],
  ['Strafe Right', 'STRAFERIGHT'],
  ['Drop', 'DROP'],
  ['Spectate', 'SPECTATE'],
  ['Upgrade Speed', 'UPGRADE1'],
  ['Upgrade Defense', 'UPGRADE2'],
  ['Upgrade Energy', 'UPGRADE3'],
  ['Upgrade Missiles', 'UPGRADE4'],
  ['Zoom Out', 'ZOOMOUT'],
  ['Default Zoom', 'DEFAULTZOOM'],
  ['Scoreboard', 'SHOWSCORE'],
  ['Main Menu', 'MAINMENU'],
  ['Keybinds', 'KEYBINDS'],
  ['Show Games', 'SHOWGAMES'],
  ['Fullscreen', 'FULLSCREEN'],
  ['Maximize Chat', 'MAXIMIZECHAT'],
  ['Minimize Chat', 'MINIMIZECHAT'],
  ['In-game Say', 'SAY'],
  ['Team Chat', 'TEAM'],
  ['Reply', 'REPLY'],
  ['Toggle Sound', 'SOUND'],
  ['Help', 'HELP'],
  ['Emotes Panel', 'EMOTES'],
  ['Invite Friends', 'INVITE'],
  ['Mouse Mode', 'MOUSEMODE'],
  ['Laser Pointer', 'LASER'],
  ['Zoom In', 'ZOOMIN'],
  ['Alternate Zoom', 'ALTZOOM'],
  ['Cruise Mode', 'CRUISE'],
  ['Hide UI', 'HIDEUI'],
  ['Clear Chat', 'CLEARCHAT'],
  ['Toggle Leaderboard', 'LEADERBOARD'],
];

/** Maps key display names to KeyboardEvent.code values (W3C UI Events spec). */
const keyCodeByName: Record<string, string> = {
  BACKSPACE: 'Backspace',
  TAB: 'Tab',
  SHIFT: 'ShiftLeft',
  CTRL: 'ControlLeft',
  ALT: 'AltLeft',
  PAUSE: 'Pause',
  CAPS: 'CapsLock',
  SPACE: 'Space',
  PGUP: 'PageUp',
  PGDOWN: 'PageDown',
  END: 'End',
  HOME: 'Home',
  LEFT: 'ArrowLeft',
  UP: 'ArrowUp',
  RIGHT: 'ArrowRight',
  DOWN: 'ArrowDown',
  INSERT: 'Insert',
  DELETE: 'Delete',
  0: 'Digit0',
  1: 'Digit1',
  2: 'Digit2',
  3: 'Digit3',
  4: 'Digit4',
  5: 'Digit5',
  6: 'Digit6',
  7: 'Digit7',
  8: 'Digit8',
  9: 'Digit9',
  A: 'KeyA',
  B: 'KeyB',
  C: 'KeyC',
  D: 'KeyD',
  E: 'KeyE',
  F: 'KeyF',
  G: 'KeyG',
  H: 'KeyH',
  I: 'KeyI',
  J: 'KeyJ',
  K: 'KeyK',
  L: 'KeyL',
  M: 'KeyM',
  N: 'KeyN',
  O: 'KeyO',
  P: 'KeyP',
  Q: 'KeyQ',
  R: 'KeyR',
  S: 'KeyS',
  T: 'KeyT',
  U: 'KeyU',
  V: 'KeyV',
  W: 'KeyW',
  X: 'KeyX',
  Y: 'KeyY',
  Z: 'KeyZ',
  WINDOWL: 'MetaLeft',
  WINDOWR: 'MetaRight',
  SELECT: 'ContextMenu',
  NUMPAD0: 'Numpad0',
  NUMPAD1: 'Numpad1',
  NUMPAD2: 'Numpad2',
  NUMPAD3: 'Numpad3',
  NUMPAD4: 'Numpad4',
  NUMPAD5: 'Numpad5',
  NUMPAD6: 'Numpad6',
  NUMPAD7: 'Numpad7',
  NUMPAD8: 'Numpad8',
  NUMPAD9: 'Numpad9',
  '*': 'NumpadMultiply',
  '+': 'NumpadAdd',
  DECIMAL: 'NumpadDecimal',
  F1: 'F1',
  F2: 'F2',
  F3: 'F3',
  F4: 'F4',
  F5: 'F5',
  F6: 'F6',
  F7: 'F7',
  F8: 'F8',
  F9: 'F9',
  F10: 'F10',
  F11: 'F11',
  F12: 'F12',
  NUMLOCK: 'NumLock',
  SCROLL: 'ScrollLock',
  ':': 'Semicolon',
  '=': 'Equal',
  ',': 'Comma',
  '-': 'Minus',
  '.': 'Period',
  '/': 'Slash',
  GRAVE: 'Backquote',
  '[': 'BracketLeft',
  '\\': 'Backslash',
  ']': 'BracketRight',
  QUOTE: 'Quote',
};

/** Right-side modifier codes that resolve to the same key name as their left-side counterpart. */
const modifierCodeAliases: Readonly<Record<string, string>> = {
  ShiftRight: 'ShiftLeft',
  ControlRight: 'ControlLeft',
  AltRight: 'AltLeft',
};

/** KeyboardEvent.code → key name: the inverse of keyCodeByName plus the modifier aliases. */
const keyNameByCode = new Map<string, string>();
for (const [name, code] of Object.entries(keyCodeByName)) {
  keyNameByCode.set(code, name);
}
for (const [rightCode, leftCode] of Object.entries(modifierCodeAliases)) {
  const name = keyNameByCode.get(leftCode);
  if (name != null) {
    keyNameByCode.set(rightCode, name);
  }
}

const PRIMARY_KEY_INDEX = 0;
const SECONDARY_KEY_INDEX = 1;

function onGamepadConnected(e: GamepadEvent) {
  uiSetGameNotification({
    type: 'alert',
    title: 'GAMEPAD CONNECTED',
    message: e.gamepad.id,
    showReconnect: false,
    timeoutMs: 3000,
  });
  isGamepadConnected = true;
  gamepadIndex = e.gamepad.index;
  gamepadState = freshGamepadState();
}

function onGamepadDisconnected(e: GamepadEvent) {
  uiSetGameNotification({
    type: 'alert',
    title: 'GAMEPAD DISCONNECTED',
    message: e.gamepad.id,
    showReconnect: false,
    timeoutMs: 3000,
  });
  isGamepadConnected = false;
  gamepadState = freshGamepadState();
}

export function setup() {
  initKeyBindsFromSettings();
  updateKeybindsList();
  // Seed uiState.keybinds (incl. bindByName) so shortcut hints are correct on
  // first paint, not only after the keybinds panel is first opened.
  refreshKeybindsUIState();

  window.addEventListener('keydown', onWindowKeyDown);
  window.addEventListener('keyup', onWindowKeyUp);
  window.addEventListener('gamepadconnected', onGamepadConnected);
  window.addEventListener('gamepaddisconnected', onGamepadDisconnected);

  if (device.mobile) {
    setupTouchDismiss();
  }
}

function onWindowKeyDown(event: KeyboardEvent) {
  if (session.state !== GAME_STATE_CODES.PLAYING && session.state !== GAME_STATE_CODES.CONNECTING) {
    return;
  }

  // Ignore keys the IME is processing (Windows emoji panel, CJK composition).
  // event.code still reports the physical key for these, so without the guard
  // a composition-commit Enter reaching the window (e.g. delivered with
  // key 'Process', which the chat input's own handler doesn't stop) would
  // pass the CHAT_PASSTHROUGH_KEYS check and toggle the chat box closed.
  if (event.isComposing || event.keyCode === 229) {
    return;
  }

  const code = event.code;
  const bind = getBind(code);

  // Any key closes the help panel except the Help bind's own, which goes on
  // to toggle it (legacy checked the H key itself, so a remapped Help key
  // closed it here and its toggle opened it again)
  if (bind !== 'HELP') {
    uiHideHelp();
  }

  if (editingBindName != null && handleKeybindChange(code)) {
    event.preventDefault();
    return;
  }

  // # opens chat with # prefilled (for QBot commands, CTF only). A bound key
  // wins: on US layouts # is Shift+3, and Shift is a default SPECIAL key, so
  // without this check tapping UPGRADE3 while boosting opened the chat box.
  if (event.key === '#' && bind == null && session.gameType === GAME_TYPES.CTF && !uiChatBoxOpen()) {
    uiControlKey('Hash', null);
    event.preventDefault();
    return;
  }

  // A held Enter: its first press opened the chat box or sent from it, and the repeats would toggle the box
  // again (the sending press is handled in the box and never reaches the latch below)
  if (event.repeat && code === 'Enter') {
    return;
  }

  if (isCapturedByChat(code)) {
    return;
  }

  if (bind == null || !MOVEMENT_BINDS.has(bind)) {
    if (!latchedControlCodes.has(code)) {
      latchedControlCodes.add(code);
      uiControlKey(code, bind);
    }
    event.preventDefault();
    return;
  }

  if (cruiseMode && bind === 'DOWN') {
    disableCruiseMode();
  } else if (cruiseMode && bind === 'UP') {
    // Cruise hands over to manual throttle: drop the flag but keep UP held on
    // the server, so the key's own release ends the thrust.
    cruiseMode = false;
    uiState.cruiseMode = false;
  }

  pressedCodes.add(code);

  // Last-input SOCD: the fresh press wins, so the opposing direction is
  // released on the server and the reversal takes effect without waiting for
  // the player to let go of the old key. Only a fresh press: a held key's
  // auto-repeat must not hand the win back.
  const opposing = OPPOSING_BIND[bind];
  if (opposing != null && !event.repeat && getSOCDMode() === 'lastInput' && isBindPhysicallyHeld(opposing)) {
    setBindHeld(opposing, false);
  }

  setBindHeld(bind, true);
  event.preventDefault();
}

function onWindowKeyUp(event: KeyboardEvent) {
  if (session.state !== GAME_STATE_CODES.PLAYING && session.state !== GAME_STATE_CODES.CONNECTING) {
    return;
  }

  const code = event.code;
  const bind = getBind(code);

  if (bind == null || !MOVEMENT_BINDS.has(bind)) {
    latchedControlCodes.delete(code);
  }

  if (isCapturedByChat(code)) {
    return;
  }

  pressedCodes.delete(code);

  if (bind != null) {
    const opposing = OPPOSING_BIND[bind];
    if (opposing != null && getSOCDMode() === 'lastInput') {
      // Another key may still hold this bind; only a full release lets go,
      // and then the opposing key, if still down, takes over as the last input left.
      if (!isBindPhysicallyHeld(bind)) {
        setBindHeld(bind, false);
        if (isBindPhysicallyHeld(opposing)) {
          setBindHeld(opposing, true);
        }
      }
    } else {
      setBindHeld(bind, false);
    }
  }

  event.preventDefault();
}

/** Keys the chat input lets through to the game while it is open (navigation and function keys). */
const CHAT_PASSTHROUGH_KEYS: ReadonlySet<string> = new Set([
  'Escape',
  'Enter',
  'ArrowLeft',
  'ArrowRight',
  'F1',
  'F2',
  'F3',
  'F4',
  'F5',
  'F6',
  'F7',
  'F8',
  'F9',
  'F10',
  'F11',
  'F12',
]);

/** Whether an open chat box owns this key, so the game must neither act on it nor preventDefault it. */
function isCapturedByChat(code: string): boolean {
  return uiChatBoxOpen() && !CHAT_PASSTHROUGH_KEYS.has(code);
}

export function toggleKeybinds() {
  if (device.mobile) {
    return;
  }

  if (uiState.activePanel === 'keybinds') {
    uiSetActivePanel(null);
    editingBindName = null;
    uiState.keybinds.editingBindName = null;
  } else {
    uiCloseAllPanels('keybinds');
    refreshKeybindsUIState();
    uiSetActivePanel('keybinds');
  }
}

function getBind(code: string): BindName | null {
  return bindByKeyCode.get(code) ?? null;
}

export function resetBinds() {
  keyBindDescription = structuredClone(DEFAULT_BINDS);
  removeSetting('keybinds');
  editingBindName = null;
  updateKeybindsList();
  refreshKeybindsUIState();
}

/**
 * Lays the saved binds over the defaults. An entry for an action that no
 * longer exists (the old DROPFLAG / DROPUPGRADE, replaced by DROP) or of the
 * wrong shape is skipped: it would map a physical key to a handler-less bind
 * and silently shadow the current bind on that key.
 */
function initKeyBindsFromSettings() {
  if (settings.keybinds == null) {
    return;
  }

  for (const [bindName, bindTuple] of Object.entries(settings.keybinds)) {
    if (isBindName(bindName) && isKeyBindTuple(bindTuple)) {
      keyBindDescription[bindName] = structuredClone(bindTuple);
    }
  }
}

/** Takes `keyName` away from whichever bind holds it, so a key only ever does one thing. */
function removeKeyFromAllBinds(keyName: string) {
  for (const bind of Object.values(keyBindDescription)) {
    if (bind[PRIMARY_KEY_INDEX] === keyName) {
      bind[PRIMARY_KEY_INDEX] = '';
    }
    if (bind[SECONDARY_KEY_INDEX] === keyName) {
      bind[SECONDARY_KEY_INDEX] = '';
    }
  }
}

/** Promotes the secondary key when the primary was cleared, and drops an emptied secondary slot. */
function cleanupEmptyBindSlots() {
  for (const bind of Object.values(keyBindDescription)) {
    const secondaryKey = bind[SECONDARY_KEY_INDEX];
    if (bind[PRIMARY_KEY_INDEX] === '' && secondaryKey != null && secondaryKey !== '') {
      bind[PRIMARY_KEY_INDEX] = secondaryKey;
      bind.splice(SECONDARY_KEY_INDEX, 1);
    } else if (secondaryKey === '') {
      bind.splice(SECONDARY_KEY_INDEX, 1);
    }
  }
}

/** The binds that differ from the defaults, which is all the settings store. */
function getChangedBindings(): KeyBindsConfig {
  const changedBinds: KeyBindsConfig = {};

  for (const bindName of BIND_NAMES) {
    const bindTuple = keyBindDescription[bindName];
    const defaultBind = DEFAULT_BINDS[bindName];
    if (bindTuple.length !== defaultBind.length || bindTuple[0] !== defaultBind[0] || bindTuple[1] !== defaultBind[1]) {
      changedBinds[bindName] = structuredClone(bindTuple);
    }
  }

  return changedBinds;
}

function saveKeybindsToSettings() {
  const changedBinds = getChangedBindings();

  if (Object.keys(changedBinds).length > 0) {
    setSettings({ keybinds: changedBinds });
  } else {
    removeSetting('keybinds');
  }
}

export function refreshKeybindsUIState() {
  const items: KeybindItem[] = [];
  // Bind name → primary-key label, for shortcut hints elsewhere in the UI
  // (toolbar tooltips, menu rows) so they track remaps. Only bound actions
  // are added; unbound ones are absent so callers can hide the hint.
  const bindByName: Partial<Record<BindName, string>> = {};

  for (const [displayName, bindName] of keybindPanelRows) {
    const bindTuple = keyBindDescription[bindName];
    const primaryKey = getKeyDisplayText(bindTuple[PRIMARY_KEY_INDEX]);
    const secondaryKey = getKeyDisplayText(bindTuple[SECONDARY_KEY_INDEX]);

    if (primaryKey != null) {
      bindByName[bindName] = primaryKey;
    }

    items.push({ displayName, bindName, primaryKey, secondaryKey });
  }

  // Split the list evenly across the two display columns by item count (left
  // takes the extra when the count is odd). Index-independent, so the columns
  // stay balanced as binds are added to or removed from keybindPanelRows.
  const splitIndex = Math.ceil(items.length / 2);
  uiState.keybinds.leftColumn = items.slice(0, splitIndex);
  uiState.keybinds.rightColumn = items.slice(splitIndex);
  uiState.keybinds.editingBindName = editingBindName;
  uiState.keybinds.editingBindIndex = editingBindIndex;
  uiState.keybinds.bindByName = bindByName;
}

/** Starts an edit of one keybind slot, the panel's click on it; the next key pressed fills the slot. Mirrored for the panel's prompt, as cancelBindEdit's refresh mirrors the end. */
export function startBindEdit(bindName: BindName, bindIndex: BindSlot) {
  editingBindName = bindName;
  editingBindIndex = bindIndex;
  uiState.keybinds.editingBindName = bindName;
  uiState.keybinds.editingBindIndex = bindIndex;
}

export function cancelBindEdit() {
  editingBindName = null;
  refreshKeybindsUIState();
}

/** Unbinds one slot (the keybinds panel's right-click), ending any edit in
 *  progress as a click elsewhere in the panel does. */
export function clearBind(bindName: BindName, bindIndex: BindSlot) {
  editingBindName = null;
  const bind = keyBindDescription[bindName];
  if (bind[bindIndex]) {
    bind[bindIndex] = '';
    cleanupEmptyBindSlots();
    updateKeybindsList();
    saveKeybindsToSettings();
  }
  refreshKeybindsUIState();
}

function handleKeybindChange(code: string): boolean {
  if (editingBindName == null) {
    return false;
  }

  // Escape backs out, leaving the key as it was. Legacy cleared the slot
  // instead, so backing out of a change to Fire left Fire unbound; clearing
  // is the panel's right-click now (clearBind)
  if (code === 'Escape') {
    cancelBindEdit();
    return true;
  }

  const keyName = keyNameByCode.get(code);
  if (keyName == null) {
    return false;
  }

  removeKeyFromAllBinds(keyName);
  keyBindDescription[editingBindName][editingBindIndex] = keyName;
  cleanupEmptyBindSlots();
  updateKeybindsList();
  saveKeybindsToSettings();
  editingBindName = null;
  refreshKeybindsUIState();

  return true;
}

/** Shown as the symbol printed on the key rather than its name. Display only:
 *  binds are still saved and matched by name. */
const KEY_SYMBOLS: Readonly<Record<string, string>> = {
  GRAVE: '`',
  QUOTE: "'",
};

function getKeyDisplayText(keyName: string | undefined): string | null {
  if (keyName == null || keyName === '') {
    return null;
  }
  return KEY_SYMBOLS[keyName] ?? keyName;
}

/** Rebuilds the code → bind lookup from the live table; a right-side modifier follows its left-side bind. */
function updateKeybindsList() {
  bindByKeyCode.clear();
  for (const bindName of BIND_NAMES) {
    for (const keyName of keyBindDescription[bindName]) {
      if (keyName == null) {
        continue;
      }
      const code = keyCodeByName[keyName];
      if (code != null) {
        bindByKeyCode.set(code, bindName);
      }
    }
  }

  for (const [rightCode, leftCode] of Object.entries(modifierCodeAliases)) {
    const leftBind = bindByKeyCode.get(leftCode);
    if (leftBind != null && !bindByKeyCode.has(rightCode)) {
      bindByKeyCode.set(rightCode, leftBind);
    }
  }
}

const GAMEPAD_BUTTON = {
  A: 0,
  B: 1,
  X: 2,
  Y: 3,
  DPAD_UP: 12,
  DPAD_DOWN: 13,
  DPAD_LEFT: 14,
  DPAD_RIGHT: 15,
} as const;

const MIN_GAMEPAD_BUTTONS = 16;
const JOYSTICK_DEADZONE = 0.2;

function isButtonPressed(gamepad: Gamepad, buttonIndex: number): boolean {
  return gamepad.buttons[buttonIndex]?.pressed ?? false;
}

/** Sends the key when the button changed since last frame, and returns the new state. */
function updateButtonState(action: KeyName, isPressed: boolean, wasPressed: boolean): boolean {
  if (wasPressed !== isPressed) {
    setBindHeld(action, isPressed);
  }
  return isPressed;
}

function normalizeAngle(angle: number): number {
  return ((angle % TWO_PI) + TWO_PI) % TWO_PI;
}

export function update() {
  // Drive the Directional scheme's proportional-turn pulse (no-op in point-to-aim
  // or when not turning). Runs before the gamepad early-return so it ticks even
  // without a gamepad connected. (Precise aim is timer-driven, not per-frame —
  // see preciseAimSteer.)
  updateDirectionalTurn();

  if (!isGamepadConnected || navigator.getGamepads == null) {
    return;
  }

  const gamepads = navigator.getGamepads();
  const gamepad = gamepads?.[gamepadIndex];
  if (gamepad == null) {
    isGamepadConnected = false;
    return;
  }

  if (gamepad.buttons.length < MIN_GAMEPAD_BUTTONS) {
    return;
  }

  const dpadUp = isButtonPressed(gamepad, GAMEPAD_BUTTON.DPAD_UP);
  const dpadDown = isButtonPressed(gamepad, GAMEPAD_BUTTON.DPAD_DOWN);
  const dpadRight = isButtonPressed(gamepad, GAMEPAD_BUTTON.DPAD_RIGHT);
  const dpadLeft = isButtonPressed(gamepad, GAMEPAD_BUTTON.DPAD_LEFT);
  const firePressed = isButtonPressed(gamepad, GAMEPAD_BUTTON.A) || isButtonPressed(gamepad, GAMEPAD_BUTTON.X);
  const specialPressed = isButtonPressed(gamepad, GAMEPAD_BUTTON.B) || isButtonPressed(gamepad, GAMEPAD_BUTTON.Y);

  gamepadState.up = updateButtonState('UP', dpadUp, gamepadState.up);
  gamepadState.down = updateButtonState('DOWN', dpadDown, gamepadState.down);
  gamepadState.right = updateButtonState('RIGHT', dpadRight, gamepadState.right);
  gamepadState.left = updateButtonState('LEFT', dpadLeft, gamepadState.left);
  gamepadState.fire = updateButtonState('FIRE', firePressed, gamepadState.fire);
  gamepadState.special = updateButtonState('SPECIAL', specialPressed, gamepadState.special);

  const axisX = gamepad.axes[1] ?? 0;
  const axisY = gamepad.axes[0] ?? 0;
  const magnitude = Math.sqrt(axisX * axisX + axisY * axisY);
  const rawAngle = -(Math.atan2(axisY, axisX) - HALF_PI) + HALF_PI;
  const normalizedAngle = normalizeAngle(rawAngle);

  if (magnitude > JOYSTICK_DEADZONE) {
    gamepadState.forward = true;
    handleRotation(normalizedAngle, magnitude);
  } else {
    if (gamepadState.forward && !dpadUp) {
      setBindHeld('UP', false);
    }
    gamepadState.forward = false;
  }
}

/** Whether `bind` is held as far as the server knows. The autopilot asks before releasing a key the player also holds. */
export function keyState(bind: BindName): boolean {
  return heldBinds.has(bind);
}

/** Whether any key is currently held on the server, from any input path. */
export function isAnyKeyHeld(): boolean {
  return heldBinds.size > 0;
}

/** The physical arrow keys: legacy let a player keep flying on them while typing in chat, whatever they are bound to. */
const ARROW_CODES: ReadonlySet<string> = new Set(['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight']);

function isAnyArrowKeyPressed(): boolean {
  for (const code of pressedCodes) {
    if (ARROW_CODES.has(code)) {
      return true;
    }
  }
  return false;
}

/** The thrust and turn keys, which clearKeys(true) keeps while an arrow key is down. */
const DIRECTION_KEYS: ReadonlySet<KeyName> = new Set<KeyName>(['UP', 'DOWN', 'LEFT', 'RIGHT']);

/**
 * Releases every network key, as when the chat opens or the window blurs.
 * With `keepArrowKeys` the thrust and turn keys stay down while a physical
 * arrow key is held (flying on the arrows while typing), and cruise keeps UP
 * either way, so a menu or chat opened mid-cruise doesn't drop the auto-throttle.
 */
export function clearKeys(keepArrowKeys = false) {
  if (session.state !== GAME_STATE_CODES.PLAYING) {
    return;
  }

  const keepDirections = keepArrowKeys && isAnyArrowKeyPressed();
  for (const key of NETWORK_KEYS) {
    if (keepDirections && DIRECTION_KEYS.has(key)) {
      continue;
    }
    if (cruiseMode && key === 'UP') {
      continue;
    }
    setBindHeld(key, false);
  }

  for (const code of pressedCodes) {
    if (!(keepArrowKeys && ARROW_CODES.has(code))) {
      pressedCodes.delete(code);
    }
  }
}

function touchCloseAll() {
  if (session.state === GAME_STATE_CODES.PLAYING) {
    uiCloseAllPanels();
  }
}

/** Legacy's Input.setupLogin: closes open panels when the game world is touched. Installed by setup on mobile. */
function setupTouchDismiss() {
  // Close open panels when the game world is touched (mobile's stand-in for
  // ESC / outside-click). Touches on the Lit UI tree must NOT blanket-close:
  // the UI owns its own dismissal (backdrop taps, the toolbar X). Without the
  // guard, any tap ON an open panel closed it at finger-down — and the
  // toolbar X then re-opened it via the synthesized click, since toggling an
  // already-closed menu opens it. (Shadow-DOM retargeting makes any touch on
  // UI content surface here with the <airmash-ui> host in its target chain.)
  // Note: no preventDefault — window touch listeners are passive by default,
  // so the legacy call here never did anything.
  window.addEventListener('touchstart', (event) => {
    if (event.target instanceof Element && event.target.closest('airmash-ui') != null) {
      return;
    }
    touchCloseAll();
  });
}

/** Whether mouse mode is on: fire and special on the mouse buttons. Legacy kept this in config. */
let mouseMode = false;

/** MOUSEMODE keybind handler. */
export function toggleMouse() {
  setMouseMode(!mouseMode);
}

/**
 * Turns mouse mode on or off (left click fires, right click uses the special),
 * saves it, and mirrors it for the menu's switch. A no-op when already in that
 * state: settings are applied at startup and again when a logged-in player's
 * remote settings arrive, and legacy's toggle there switched it straight back
 * off and deleted the saved setting. `notify` shows the in-game notice, which
 * the menu's switch doesn't need.
 */
export function setMouseMode(enabled: boolean, notify = true) {
  if (device.mobile || mouseMode === enabled) {
    return;
  }

  mouseMode = enabled;
  uiState.gameSettings.mouseMode = enabled;

  window.removeEventListener('mousedown', mouseDown);
  window.removeEventListener('mouseup', mouseUp);

  if (enabled) {
    window.addEventListener('mousedown', mouseDown);
    window.addEventListener('mouseup', mouseUp);
    setSettings({
      mousemode: true,
    });
  } else {
    removeSetting('mousemode');
  }

  if (!notify) {
    return;
  }

  if (enabled) {
    uiSetGameNotification({
      type: 'alert',
      title: 'MOUSE MODE',
      showReconnect: false,
      timeoutMs: 7000,
      message: 'Enabled\nLMB: Fire    RMB: Special    WASD: Move',
    });
  } else {
    uiSetGameNotification({
      type: 'alert',
      title: 'MOUSE MODE',
      message: 'Disabled',
      showReconnect: false,
      timeoutMs: 3000,
    });
  }
}

function mouseDown(event: MouseEvent) {
  if (event.button !== 0 && event.button !== 2) {
    return;
  }

  if (event.target instanceof HTMLCanvasElement) {
    const action = event.button === 0 ? 'FIRE' : 'SPECIAL';
    setBindHeld(action, true);
    event.preventDefault();
  }
}

function mouseUp(event: MouseEvent) {
  if (event.button !== 0 && event.button !== 2) {
    return;
  }

  if (event.target instanceof HTMLCanvasElement) {
    const action = event.button === 0 ? 'FIRE' : 'SPECIAL';
    setBindHeld(action, false);
    event.preventDefault();
  } else {
    setBindHeld('FIRE', false);
    setBindHeld('SPECIAL', false);
  }
}

/**
 * Reports a bind pressed or released from any input path and tells the server
 * what it needs to hear: a network key as itself, a Mohawk's strafe as SPECIAL
 * plus a turn, and the rest only remembered as held (strafing on another plane
 * does nothing, and control keys never come here). A press is the player
 * taking over, so it cancels the autopilot first, whose cancel releases that
 * autopilot's own keys, and it is the signal SpectatorOnStart's grace window
 * waits for. Deduped against the held set, so a repeat changes nothing.
 */
function setBindHeld(bind: BindName, pressed: boolean): void {
  if (pressed) {
    cancelAutoPilotOnUserInput(bind);
    spectatorOnStartHandleMovementIntent();
  }

  if (heldBinds.has(bind) === pressed) {
    return;
  }
  if (pressed) {
    heldBinds.add(bind);
  } else {
    heldBinds.delete(bind);
  }

  if (bind === 'STRAFELEFT' || bind === 'STRAFERIGHT') {
    if (session.myType !== PlaneType.Mohawk) {
      return;
    }
    const turn: KeyName = bind === 'STRAFELEFT' ? 'LEFT' : 'RIGHT';
    if (pressed) {
      setBindHeld('SPECIAL', true);
      setBindHeld(turn, true);
    } else {
      setBindHeld(turn, false);
      // SPECIAL stays down while the other strafe key still holds it.
      const otherStrafe: BindName = bind === 'STRAFELEFT' ? 'STRAFERIGHT' : 'STRAFELEFT';
      if (!heldBinds.has(otherStrafe)) {
        setBindHeld('SPECIAL', false);
      }
    }
    return;
  }

  if (isKeyName(bind)) {
    sendKeyToServer(bind, pressed);
  }
}

/** Every key this layer sends goes through here. A release is held back while
 *  the autopilot holds the same key, so letting go of UP after starting it
 *  doesn't cut its thrust (it re-presses only keys its own record says are up).
 *  The autopilot does the same for keys held here; see AutoPilot's pressKey. */
function sendKeyToServer(keyName: KeyName, isPressed: boolean) {
  if (!isPressed && isAutoPilotHolding(keyName)) return;
  networkSendKey(keyName, isPressed);
}

/** Stick force (touch) / stick magnitude (gamepad) at or above which thrust (UP) is asserted. */
const THRUST_FORCE_THRESHOLD = 0.5;

/** Directional scheme. Thrust is BINARY (full, like the keyboard) across the
 *  forward arc; turning is proportional to how far the stick LEANS off the
 *  up↔down axis — by direction/ANGLE, not raw deflection. That's the crucial
 *  part: a diagonal push splits its force between the axes, so a component-based
 *  turn gave "forward + turn" a weak fraction of each (a thumb-straining drag).
 *  Reading the lean direction instead means a gentle up-and-right push keeps
 *  FULL thrust AND turns — no force-splitting, no reaching for the corner.
 *  Thresholds below are on the unit direction (sin/cos of the angle from "up"),
 *  so they're force-independent. Feel knobs — tune on-device. */
const DIRECTIONAL_MIN_FORCE = 0.3; // stick deadzone — push out this far before anything engages
const DIRECTIONAL_FORWARD_DOT = -0.25; // thrust UP while the push is at least this "up" (cos of angle-from-up; ~104° arc)
const DIRECTIONAL_REVERSE_DOT = -0.55; // thrust DOWN (reverse) once the push is clearly back (~123°+)
const DIRECTIONAL_TURN_LEAN_DEADZONE = 0.2; // |lean| below this = go straight (~±11° of the up/down axis)
const DIRECTIONAL_TURN_LEAN_FULL = 0.85; // lean at/above which the turn runs full-rate (~±58°)

/** Directional turn is proportional, not all-or-nothing. Lean past the deadzone
 *  up to ...FULL maps to a PWM duty cycle (the turn rate). A binary LEFT/RIGHT
 *  key can't turn at a fraction of the ship's turnFactor, so
 *  `updateDirectionalTurn()` holds the key for `duty` of each PWM period and
 *  releases it for the rest — client rotation interpolation smooths the pulsing.
 *  This is what lets you hold a gentle arc instead of snapping to full-rate. */
const DIRECTIONAL_TURN_MIN_DUTY = 0.2; // duty just past the deadzone (finest turn)
const DIRECTIONAL_TURN_PWM_PERIOD = 90; // ms per on/off cycle

let directionalTurnDir: 'LEFT' | 'RIGHT' | null = null;
let directionalTurnDuty = 0;
let directionalTurnHeldDir: 'LEFT' | 'RIGHT' | null = null;
let directionalTurnPeriodStart = 0;

/** Within this many radians of exactly-opposite (±π), the LEFT/RIGHT turn choice is
 *  ambiguous and flip-flops on jitter — so we latch the in-progress direction. */
const TURN_REVERSE_MARGIN = 0.4;

/** Precise aim (opt-in, touch point-to-aim only): "throw, look once, correct
 *  once, stop." The original handleRotation below is open-loop — one timed
 *  turn pulse per stick event with the full round-trip ping subtracted as
 *  lead-compensation — which parks a ping-worth short of the target (aim 6
 *  o'clock, sit at ~5; the residual computes to a <10ms pulse and is dropped)
 *  and keeps turning the wrong way after a direction flip until its stale
 *  timer expires.
 *
 *  A continuous closed-loop controller was tried first and OSCILLATED:
 *  everything the client can see is a round-trip stale, so a loop that keeps
 *  correcting chases the server's echo of its own past around the setpoint —
 *  a delayed-actuation limit cycle; three tunings (deadzone, park hysteresis,
 *  zero-crossing park) each just moved the wiggle. This design is
 *  oscillation-proof by construction because it stops reacting: per aim it
 *  fires ONE smooth timed pulse (no ping model — press and release travel
 *  equally late, so the server-side hold duration matches the client's),
 *  waits out the round trip, measures the residual ONCE, spends at most ONE
 *  small correction pulse, then goes quiet until the stick genuinely moves.
 *  Max two throws per aim — a system that stops reacting cannot oscillate,
 *  the same way cruise control (which never reacts) never does. The ±π
 *  reversal latch is unnecessary here: each aim decides its direction once
 *  and commits, and the intent gate keeps thumb jitter from re-deciding.
 *
 *  The one subtlety is WHERE a throw measures from. Our own plane renders
 *  from the server echo (keystate is only ever written by network decode —
 *  Player.ts), so while a commanded turn is in flight, player.rot is a full
 *  round trip behind where the plane will be when the next command lands. A
 *  re-aim mid-turn computed from player.rot therefore overshoots by
 *  ping × turn-rate — and since the correction pulse repeats the mistake, it
 *  lands the same distance past on the OTHER side (the symmetric
 *  over-then-under two-step). This staleness is also what the original's
 *  `- ping` was really compensating, which is why the original over-corrects
 *  throws that start from rest and parks short. The fix is not ping
 *  arithmetic but dead reckoning: we know exactly what we commanded, so a
 *  throw that starts mid-flight measures from the in-flight pulse's own
 *  anchor (base + dir × rate × held-so-far). Latency cancels exactly — the
 *  old release and the new press ride the same wire, so the server holds the
 *  old key for precisely the client-side elapsed time. Ping only decides how
 *  long to wait before the look re-anchors on the echo, the measured truth
 *  that mops up reckoning drift, timer lateness, and rate mismatch. A sweep
 *  re-bases the SAME held key every event — no packet churn, one continuous
 *  smooth turn whose release deadline keeps being refined. Commands can also
 *  be LOST, not just late — see PRECISE_AIM_REPULSE_MS (fewer, longer pulses)
 *  and the reality clamp in preciseAimSteer (reckoning may never claim more
 *  than the echo could physically be hiding). */
const PRECISE_AIM_INTENT = 0.04; // rad (~2.3°) — stick moves past this are a new aim; below is thumb jitter
const PRECISE_AIM_TOLERANCE = 0.035; // rad (~2°) — close enough for a live throw; don't chase less
// Eager in, lazy out: throws answer the stick at TOLERANCE, but the look only
// spends its correction on a miss BIGGER than what a thumb can express (the
// intent band) — a 2-4° residual left standing is invisible; an unprompted
// correction nudge a beat after parking is not.
const PRECISE_AIM_CORRECTION_MIN = 0.07; // rad (~4°)
// Pursuit cadence: when NOT already holding the key, start a new pulse at
// most once per this window — the original's 100ms rotation throttle,
// resurrected for the load-bearing work it was quietly doing. A slow sweep
// otherwise becomes stop-start micro-pulses (press+release ~11ms apart every
// ~40ms), and on a batching mobile uplink or rate-limiting server both
// packets can land inside one physics tick — a press already released by
// tick time turns the plane NOTHING. The reckoning credits it anyway, so the
// loss accumulates all sweep long and the end-of-sweep look swings the whole
// debt at once. Fewer, longer pulses deliver; extending an already-held
// pulse is packet-free and never gated.
const PRECISE_AIM_REPULSE_MS = 100;
const PRECISE_AIM_SETTLE_EXTRA_MS = 80; // the look waits ping + this after release (the echo must come home)
let preciseAimTarget: number | null = null;
let preciseAimHeldDir: 'LEFT' | 'RIGHT' | null = null;
let preciseAimPulseTimer: number | null = null;
let preciseAimLookTimer: number | null = null;
let preciseAimLookSpent = false;
// Dead-reckoning anchor for the in-flight (or just-released) pulse: the server
// rotation it turns FROM, which way, when it (re)based, and when it actually
// released (null while held). See the header — this is how a mid-flight
// re-aim measures where the plane WILL be instead of the round-trip-stale echo.
let preciseAimPulseBase: number | null = null;
let preciseAimPulseSign: 1 | -1 = 1;
let preciseAimPulseStart = 0;
let preciseAimReleasedAt: number | null = null;
let preciseAimLastPulseStart = 0;
let preciseAimDeferTimer: number | null = null;

/** Folds an angle difference into [-π, π]. */
function foldAngleDiff(diff: number): number {
  if (diff > Math.PI) return diff - TWO_PI;
  if (diff < -Math.PI) return diff + TWO_PI;
  return diff;
}

function isPreciseAim(): boolean {
  return (settings.game?.preciseAim ?? GAME_DEFAULTS.preciseAim) && !isDirectionalScheme();
}

/** Ends the in-flight pulse: clears its timer and releases the held turn key,
 *  stamping the ACTUAL release time so setTimeout lateness can't poison the
 *  reckoning. */
function preciseAimReleaseTurn() {
  if (preciseAimPulseTimer != null) {
    window.clearTimeout(preciseAimPulseTimer);
    preciseAimPulseTimer = null;
  }
  if (preciseAimHeldDir != null) {
    setBindHeld(preciseAimHeldDir, false);
    preciseAimHeldDir = null;
    preciseAimReleasedAt = performance.now();
  }
}

/** Cancels any in-flight pulse/look and releases a held turn key — "abandon
 *  the aim". Called on stick release, touchcancel, and key-wipe. (Re-aims no
 *  longer come through here: preciseAimSteer re-bases the live pulse
 *  instead.) The reckoning anchor deliberately survives — a quick re-tap
 *  inside the settle window must still measure from where the plane really
 *  is, not from the echo that hasn't shown the last turn yet. */
function preciseAimReset() {
  if (preciseAimLookTimer != null) {
    window.clearTimeout(preciseAimLookTimer);
    preciseAimLookTimer = null;
  }
  if (preciseAimDeferTimer != null) {
    window.clearTimeout(preciseAimDeferTimer);
    preciseAimDeferTimer = null;
  }
  preciseAimReleaseTurn();
}

/** Ends the in-flight pulse now and, if this aim hasn't spent its one look
 *  yet, schedules it: after ping + settle the echo is the truth, so the look
 *  re-anchors on player.rot and spends the single correction. */
function preciseAimEndPulse() {
  preciseAimReleaseTurn();
  if (!preciseAimLookSpent && preciseAimLookTimer == null) {
    preciseAimLookTimer = window.setTimeout(
      () => {
        preciseAimLookTimer = null;
        preciseAimLookSpent = true;
        preciseAimSteer(true);
      },
      Math.round(networkTiming.ping) + PRECISE_AIM_SETTLE_EXTRA_MS,
    );
  }
}

/** The steering engine: aims the turn pulse at the current target. Called on
 *  every genuine re-aim and once by the look. Measures from the best-known
 *  rotation — the dead-reckoning anchor while our own turn is in (or freshly
 *  out of) flight, the settled echo otherwise — and (re)schedules the release
 *  deadline. setBindHeld dedupes, so the same press pair serves fresh
 *  press, direction flip, and same-direction re-base with no redundant
 *  packets. `isLook` applies the lazy correction bar (see
 *  PRECISE_AIM_CORRECTION_MIN). */
function preciseAimSteer(isLook = false) {
  if (preciseAimTarget == null) {
    return;
  }
  const player = playersGetMe();
  if (player == null) {
    return;
  }
  const turnRate = 60 * SHIPS[player.type].turnFactor; // rad/s

  const now = performance.now();
  let baseRot = player.rot;
  if (preciseAimPulseBase != null) {
    if (preciseAimHeldDir != null) {
      // Mid-flight: the plane WILL have turned held-so-far by the time the
      // next command lands (the release and the new press ride the same wire).
      baseRot = normalizeAngle(
        preciseAimPulseBase + preciseAimPulseSign * turnRate * ((now - preciseAimPulseStart) / 1000),
      );
    } else if (
      preciseAimReleasedAt != null &&
      now - preciseAimReleasedAt < Math.round(networkTiming.ping) + PRECISE_AIM_SETTLE_EXTRA_MS
    ) {
      // Just released: the echo hasn't shown the whole turn yet — reckon the
      // full actual hold. Past the settle window, player.rot is the truth.
      baseRot = normalizeAngle(
        preciseAimPulseBase + preciseAimPulseSign * turnRate * ((preciseAimReleasedAt - preciseAimPulseStart) / 1000),
      );
    }
    // Reality clamp: the plane can only be ahead of its echo by what one
    // round trip of full-rate turning could hide (the echo is ~RTT/2 old and
    // our commands land ~RTT/2 later). A chained anchor claiming more means
    // the server under-delivered (see PRECISE_AIM_REPULSE_MS) — pull the
    // base back to what the echo can vouch for. The re-grown diff then
    // commands longer pulses, which DO deliver: under-delivery self-corrects
    // during the sweep instead of piling up for one big swing at the end.
    const maxLead = (turnRate * (Math.round(networkTiming.ping) + PRECISE_AIM_SETTLE_EXTRA_MS)) / 1000;
    const echoGap = foldAngleDiff(baseRot - player.rot);
    if (Math.abs(echoGap) > maxLead) {
      baseRot = normalizeAngle(player.rot + Math.sign(echoGap) * maxLead);
    }
  }

  const angleDiff = foldAngleDiff(preciseAimTarget - baseRot);
  const holdMs = Math.round((Math.abs(angleDiff) / turnRate) * 1000);
  const tolerance = isLook ? PRECISE_AIM_CORRECTION_MIN : PRECISE_AIM_TOLERANCE;
  // Close enough (or below what the server meaningfully registers — same
  // 10ms floor as the original): end any live pulse here and stay quiet.
  if (Math.abs(angleDiff) <= tolerance || holdMs < 10) {
    if (preciseAimHeldDir != null) {
      preciseAimEndPulse();
    }
    return;
  }

  // Pursuit cadence gate (see PRECISE_AIM_REPULSE_MS): starting a NEW pulse
  // is budgeted; defer the retry to the budget's expiry so a paused sweep
  // can't strand mid-aim. Extending/flipping a live hold passes freely.
  if (preciseAimHeldDir == null && now - preciseAimLastPulseStart < PRECISE_AIM_REPULSE_MS) {
    if (preciseAimDeferTimer == null) {
      preciseAimDeferTimer = window.setTimeout(
        () => {
          preciseAimDeferTimer = null;
          preciseAimSteer();
        },
        PRECISE_AIM_REPULSE_MS - (now - preciseAimLastPulseStart),
      );
    }
    return;
  }
  // A starting pulse also supersedes a pending deferred retry — without this
  // a look's correction could be followed by a stray third throw.
  if (preciseAimDeferTimer != null) {
    window.clearTimeout(preciseAimDeferTimer);
    preciseAimDeferTimer = null;
  }

  // A (re)scheduled pulse supersedes a pending look — the release will book
  // a fresh one that waits out THIS pulse's echo.
  if (preciseAimLookTimer != null) {
    window.clearTimeout(preciseAimLookTimer);
    preciseAimLookTimer = null;
  }

  const turnDirection: 'LEFT' | 'RIGHT' = angleDiff > 0 ? 'RIGHT' : 'LEFT';
  setBindHeld(turnDirection === 'RIGHT' ? 'LEFT' : 'RIGHT', false);
  setBindHeld(turnDirection, true);
  preciseAimHeldDir = turnDirection;
  preciseAimLastPulseStart = now;
  preciseAimPulseBase = baseRot;
  preciseAimPulseSign = angleDiff > 0 ? 1 : -1;
  preciseAimPulseStart = now;
  preciseAimReleasedAt = null;
  if (preciseAimPulseTimer != null) {
    window.clearTimeout(preciseAimPulseTimer);
  }
  preciseAimPulseTimer = window.setTimeout(() => {
    preciseAimPulseTimer = null;
    preciseAimEndPulse();
  }, holdMs);
}

function handleRotation(rotation: number, touchForce?: number) {
  const player = playersGetMe();
  if (player == null) {
    return;
  }

  const angleDiff = foldAngleDiff(rotation - player.rot);

  const turnFactor = SHIPS[player.type].turnFactor;
  let rotationDuration = Math.round((Math.abs(angleDiff) / (60 * turnFactor)) * 1000);

  rotationDuration -= Math.round(networkTiming.ping);

  if (rotationDuration < 10 || clock.time - lastRotationTime < 100) {
    return;
  }

  if (maybeRotationTimerId != null) {
    window.clearTimeout(maybeRotationTimerId);
  }

  lastRotationTime = clock.time;

  // Pick the turn direction toward the target. Near ±180° the sign of angleDiff
  // flips on the slightest jitter (left vs right to reach exactly-opposite is a
  // tie), which flip-flops the turn key and makes the ship wiggle — so in that
  // ambiguous zone we commit to the latched direction and rotate through. Once
  // the angle is unambiguous again the latch clears, so normal aiming is exact.
  let turnDirection: 'LEFT' | 'RIGHT' = angleDiff > 0 ? 'RIGHT' : 'LEFT';
  if (Math.abs(angleDiff) > Math.PI - TURN_REVERSE_MARGIN) {
    turnDirection = activeTurnDirection ?? turnDirection;
    activeTurnDirection = turnDirection;
  } else {
    activeTurnDirection = null;
  }
  const oppositeDirection = turnDirection === 'RIGHT' ? 'LEFT' : 'RIGHT';

  // Cruise owns UP (auto-throttle); don't let stick force release it, or cruise
  // would silently die while its button still reads active. Cancel via the button.
  const shouldMoveForward = touchForce == null || touchForce >= THRUST_FORCE_THRESHOLD;
  if (!cruiseMode) {
    setBindHeld('UP', shouldMoveForward);
  }
  setBindHeld(turnDirection, true);
  setBindHeld(oppositeDirection, false);

  maybeRotationTimerId = window.setTimeout(() => {
    setBindHeld(turnDirection, false);
  }, rotationDuration);
}

/** Whether the opt-in Directional touch scheme is active (default is point-to-aim). */
function isDirectionalScheme(): boolean {
  return settings.game?.controlScheme === 'directional';
}

/**
 * Directional scheme: thrust is binary (full, like the keyboard) across the
 * forward arc, and turning is proportional to how far the stick LEANS off the
 * up↔down axis — by direction, not raw deflection, so a diagonal keeps full
 * thrust AND turns instead of splitting its force. `forwardness`/`lean` are the
 * cos/sin of the angle from straight-up, i.e. the unit direction (force-
 * independent); `force` only gates the deadzone. Cruise + reverse come for free;
 * we mirror the keyboard's cruise-cancel (see onWindowKeyDown): a backward push
 * cancels cruise (and reverses), a forward push takes over manual throttle, and
 * pure steering leaves cruise's UP latch intact.
 */
function touchDirectionalMove(angleRadian: number, force: number) {
  // Angle relative to straight-up: dev = 0 is forward, ±π is back, sign = lean.
  const dev = foldAngleDiff(HALF_PI - angleRadian);
  const forwardness = Math.cos(dev); // 1 = straight up, 0 = pure side, -1 = straight down
  const lean = Math.sin(dev); // signed sideways direction: + = right, - = left

  const engaged = force >= DIRECTIONAL_MIN_FORCE;
  const up = engaged && forwardness >= DIRECTIONAL_FORWARD_DOT;
  const down = engaged && forwardness <= DIRECTIONAL_REVERSE_DOT;

  if (cruiseMode && down) {
    disableCruiseMode(); // releases UP + clears cruise; DOWN asserted below
  } else if (cruiseMode && up) {
    cruiseMode = false; // manual-throttle takeover; UP asserted below
    uiState.cruiseMode = false;
  }

  // Turn: proportional to lean ANGLE (not deflection), so a gentle up-and-right
  // push turns gently at full thrust. updateDirectionalTurn() pulses the key from
  // the duty set here. Steering never touches UP, so cruise's latch survives a trim.
  const leanMag = Math.abs(lean);
  if (engaged && leanMag >= DIRECTIONAL_TURN_LEAN_DEADZONE) {
    directionalTurnDir = lean > 0 ? 'RIGHT' : 'LEFT';
    const t =
      (leanMag - DIRECTIONAL_TURN_LEAN_DEADZONE) / (DIRECTIONAL_TURN_LEAN_FULL - DIRECTIONAL_TURN_LEAN_DEADZONE);
    directionalTurnDuty = DIRECTIONAL_TURN_MIN_DUTY + (1 - DIRECTIONAL_TURN_MIN_DUTY) * Math.min(Math.max(t, 0), 1);
  } else {
    directionalTurnDir = null;
  }

  // While cruise still holds UP (neutral or horizontal stick) don't fight its
  // latch — only drive UP/DOWN once cruise is off. Mirrors the keyboard, where a
  // held-UP cruise isn't released until you press DOWN or let the key go.
  if (!cruiseMode) {
    setBindHeld('UP', up);
    setBindHeld('DOWN', down);
  }
}

/** Directional scheme stick release: drop steering + reverse, but keep cruise's
 *  UP latch (like lifting off the keyboard movement keys while cruising). */
function touchDirectionalEnd() {
  directionalTurnDir = null;
  if (directionalTurnHeldDir != null) {
    setBindHeld(directionalTurnHeldDir, false);
    directionalTurnHeldDir = null;
  }
  setBindHeld('DOWN', false);
  if (!cruiseMode) {
    setBindHeld('UP', false);
  }
}

/**
 * Per-frame pulse driver for the Directional scheme's proportional turn. Holds
 * the active turn key for `directionalTurnDuty` of each PWM period and releases
 * it for the rest, approximating an analog turn rate from a binary key. A held
 * stick fires no move events, so this runs from the main loop's inputUpdate().
 * No-op (and cheap) when not turning, so it's safe to call every frame in either
 * scheme — point-to-aim never sets `directionalTurnDir`.
 */
function updateDirectionalTurn() {
  let desiredDir: 'LEFT' | 'RIGHT' | null = null;

  if (directionalTurnDir != null) {
    const elapsed = clock.time - directionalTurnPeriodStart;
    if (elapsed < 0 || elapsed >= DIRECTIONAL_TURN_PWM_PERIOD) {
      directionalTurnPeriodStart = clock.time;
    }
    const phase = (clock.time - directionalTurnPeriodStart) / DIRECTIONAL_TURN_PWM_PERIOD;
    if (phase < directionalTurnDuty) {
      desiredDir = directionalTurnDir;
    }
  }

  if (desiredDir !== directionalTurnHeldDir) {
    if (directionalTurnHeldDir != null) {
      setBindHeld(directionalTurnHeldDir, false);
    }
    if (desiredDir != null) {
      setBindHeld(desiredDir, true);
    }
    directionalTurnHeldDir = desiredDir;
  }
}

/**
 * Touch joystick movement → ship rotation/throttle. Driven from the
 * `<touch-controls>` Lit component via GameAPI (the component owns the nipplejs
 * widget; this keeps the angle→key translation game-side and identical to the
 * legacy path). Dispatches by control scheme: the default point-to-aim path
 * below, or `touchDirectionalMove` when the Directional scheme is opted in.
 * `angleRadian` is nipplejs' `data.angle.radian`.
 */
export function touchJoystickMove(angleRadian: number, force: number) {
  if (isDirectionalScheme()) {
    touchDirectionalMove(angleRadian, force);
    return;
  }

  const normalizedAngle = normalizeAngle(HALF_PI - angleRadian);

  // Thrust, decoupled from steering: assert UP every move purely on stick force,
  // independent of handleRotation's angle-delta gate / 100ms throttle. That
  // coupling was the "slam a direction → ship stays still for ~1s" lag — on a
  // big turn or a slow sweep handleRotation early-returns and thrust was never
  // (re)asserted. setBindHeld dedupes, so re-asserting every move is cheap.
  // While cruising, cruise owns UP — don't let a light touch drop it.
  if (!cruiseMode) {
    setBindHeld('UP', force >= THRUST_FORCE_THRESHOLD);
  }

  if (isPreciseAim()) {
    // "Throw, look once, correct once, stop": moves inside the intent band
    // are thumb jitter — the committed throw keeps flying. A genuine re-aim
    // re-bases the live pulse (or flips/throws fresh) toward the new heading
    // and earns a fresh look.
    if (preciseAimTarget != null && Math.abs(foldAngleDiff(normalizedAngle - preciseAimTarget)) <= PRECISE_AIM_INTENT) {
      return;
    }
    preciseAimTarget = normalizedAngle;
    preciseAimLookSpent = false;
    preciseAimSteer();
    return;
  }

  handleRotation(normalizedAngle, force);
}

/** Touch fire/special button press/release → key update (driven via GameAPI). */
export function touchButton(action: 'FIRE' | 'SPECIAL', pressed: boolean) {
  setBindHeld(action, pressed);
}

/**
 * Touch one-shot action button (e.g. DROP) → dispatch through the same
 * `controlKey` path the keyboard uses, so the contextual behaviour (drop the
 * flag in CTF, a powerup otherwise) stays in one place. Fired once per tap;
 * `controlKey` already no-ops unless the game is PLAYING. The empty `code` is
 * harmless — the DROP branch keys off `bindName` alone.
 */
export function touchAction(bindName: BindName) {
  uiControlKey('', bindName);
}

/** Release every network key — touchcancel safety net for interrupted touches,
 *  also fired when the touch controls hide (menu/chat drawer opening). */
export function touchCancelRelease() {
  for (const key of NETWORK_KEYS) {
    // Cruise owns UP (same guard as the keyboard release-all and the two stick
    // ends): opening a menu/chat mid-cruise must not silently drop the
    // auto-throttle while its button still reads active — you should keep
    // flying while you read/type. Cancel stays with the button (and
    // kill/respawn, which disable cruise network-side).
    if (cruiseMode && key === 'UP') {
      continue;
    }
    setBindHeld(key, false);
  }
  // Clear the proportional-turn pulse state (and precise aim's throws) so
  // nothing keeps steering after the stick's touch was interrupted (no further
  // move/end events arrive).
  preciseAimTarget = null;
  preciseAimReset();
  directionalTurnDir = null;
  directionalTurnHeldDir = null;
}

/** Touch joystick release (point-to-aim) → drop steering but keep cruise's UP
 *  latch, so lifting the stick while cruising keeps you flying (tap cruise to
 *  cancel). Mirrors the directional end. Driven via GameAPI. */
export function touchJoystickEnd() {
  if (isDirectionalScheme()) {
    touchDirectionalEnd();
    return;
  }
  // Precise aim: cancel any in-flight pulse/look and drop the target — a
  // lifted stick means "stop aiming", not "finish the throw".
  preciseAimTarget = null;
  preciseAimReset();
  setBindHeld('LEFT', false);
  setBindHeld('RIGHT', false);
  if (!cruiseMode) {
    setBindHeld('UP', false);
  }
}

/** Whether the window has focus: the focus and blur listeners below keep it, and index reads it to give a blurred window an occasional frame. */
let windowFocused = true;

export function hasFocus(): boolean {
  return windowFocused;
}

export function gameFocus(): void {
  windowFocused = true;
}

/** The window lost focus: release everything, and forget which control keys
 *  were down, since their keyup will never arrive. */
export function gameBlur() {
  windowFocused = false;
  clearKeys();
  latchedControlCodes.clear();
}

export function toggleCruiseMode() {
  cruiseMode = !cruiseMode;
  setBindHeld('UP', cruiseMode);
  uiState.cruiseMode = cruiseMode;
}

export function disableCruiseMode() {
  if (cruiseMode) {
    cruiseMode = false;
    setBindHeld('UP', false);
    uiState.cruiseMode = false;
  }
}

export function wipe() {
  if (maybeRotationTimerId != null) {
    window.clearTimeout(maybeRotationTimerId);
    maybeRotationTimerId = null;
  }

  heldBinds.clear();
  pressedCodes.clear();
  latchedControlCodes.clear();

  lastRotationTime = 0;
  gamepadState = freshGamepadState();
  editingBindName = null;
  editingBindIndex = 0;
  cruiseMode = false;
  uiState.cruiseMode = false;
  preciseAimTarget = null;
  // Drop the reckoning anchor too: the wipe accompanies state changes where
  // the plane's rotation teleports (respawn/game switch), so it's no longer true.
  preciseAimPulseBase = null;
  preciseAimReset();
  directionalTurnDir = null;
  directionalTurnHeldDir = null;
}
